<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BtnIssueInvoice',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'success']);

  const props = defineProps({
    invoice: {
      type: Object,
      required: true,
    },
    confirmMessage: {
      type: String,
      required: false,
    },
    size: {
      type: String,
      required: false,
    },
    target: {
      type: String,
      required: false,
      default: 'self',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    bntText: $fn.value.pgettext('BtnIssueInvoice', 'Issue invoice'),
    btnTooltip: $fn.value.pgettext(
      'BtnIssueInvoice',
      'Finalize and issue the invoice.'
    ),
    confirmHeader: $fn.value.pgettext('BtnIssueInvoice', 'Issue invoice'),
    confirmText: $fn.value.pgettext(
      'BtnIssueInvoice',
      'Please confirm issuing the invoice. The issued invoice can no longer be modified.'
    ),
    btnOk: $fn.value.pgettext('BtnIssueInvoice', 'Issue invoice'),
    btnCancel: $fn.value.pgettext('BtnIssueInvoice', 'Cancel'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.confirmMessage,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('confirmMessage');
    }
  );
  watch(
    () => props.size,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('size');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );

  const act_7315092382398562_emit_e1dphc5th_getActionArgs = async ($event) => {
    return {
      event: 'success',
    };
  };
  const act_7315092382398562_request_fdsv6e0iu_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_invoice_status',
      data: {
        id: props.invoice.id,
        status: 'completed',
        etag: props.invoice.etag,
      },
    };
  };
  const act_7315092382398562_request_fdsv6e0iu_getConfirm = async ($event) => {
    return {
      header: messages.value.confirmHeader,
      message: props.confirmMessage || messages.value.confirmText,
      btnOk: messages.value.btnOk,
      btnCancel: messages.value.btnCancel,
      severity: 'warning',
    };
  };

  const act_7315092382398562_emit_e1dphc5th = new actionTypes.EmitAction({
    actionArgs: act_7315092382398562_emit_e1dphc5th_getActionArgs,
  });
  const inst_evh_7315092382398562_success_sr6bs57dk =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_emit_e1dphc5th,
      event: 'success',
      displayName: 'emit',
    });
  const act_7315092382398562_request_fdsv6e0iu = new actionTypes.RequestAction({
    actionArgs: act_7315092382398562_request_fdsv6e0iu_getActionArgs,
    confirm: act_7315092382398562_request_fdsv6e0iu_getConfirm,
    displayName: 'submit',
    events: [inst_evh_7315092382398562_success_sr6bs57dk],
  });
  const inst_evh_7315092382398562_clickPreventStop_k46rk4k1j =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_request_fdsv6e0iu,
      event: 'click.prevent.stop',
      displayName: 'submit',
    });
  const inst_evh_7315092382398562_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_clickPreventStop_k46rk4k1j],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_success_sr6bs57dk,
    inst_evh_7315092382398562_clickPreventStop_k46rk4k1j,
    submit: inst_evh_7315092382398562_clickPreventStop_k46rk4k1j,
    inst_evh_7315092382398562_clickPreventStop,
  };

  const evh_7315092382398562_success_sr6bs57dk = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_success_sr6bs57dk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_clickPreventStop_k46rk4k1j = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop_k46rk4k1j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpButton
    :spinning="actions.submit.isRunning"
    variant="primary"
    :size="size"
    :text="messages.bntText"
    :tooltip="messages.btnTooltip"
    @click.prevent.stop="evh_7315092382398562_clickPreventStop($event, {})"
  >
  </ItpButton>
</template>
